import React from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Button, Card, Form, Grid, Select, Space, Switch, message } from 'antd'
import {
  listCaptains,
  patchCaptain,
  getCaptainPreferredLocations,
  setCaptainPreferredLocations,
} from '../../api/adminCaptains'
import { listDeliveryLocations } from '../../api/deliveryLocations'

const { useBreakpoint } = Grid

export default function AdminCaptainEdit() {
  const { id } = useParams()
  const userId = Number(id)
  const navigate = useNavigate()
  const screens = useBreakpoint()
  const isMobile = !screens.md
  const [loading, setLoading] = React.useState(true)
  const [saving, setSaving] = React.useState(false)
  const [captain, setCaptain] = React.useState(null)
  const [locations, setLocations] = React.useState([])
  const [form] = Form.useForm()

  async function load() {
    setLoading(true)
    try {
      const [rows, locs, preferred] = await Promise.all([
        listCaptains(),
        listDeliveryLocations(),
        getCaptainPreferredLocations(userId),
      ])
      const me = (Array.isArray(rows) ? rows : []).find(r => r.id === userId)
      if (!me) {
        message.error('الكابتن غير موجود')
        navigate('/admin/captains')
        return
      }
      setCaptain(me)
      setLocations(Array.isArray(locs) ? locs : [])
      form.setFieldsValue({
        isCaptain: me.isCaptain,
        preferredLocationIds: (Array.isArray(preferred) ? preferred : []).map(p => p.id),
      })
    } catch (e) {
      console.error(e)
      message.error('تعذر التحميل')
    } finally {
      setLoading(false)
    }
  }
  React.useEffect(() => { load() }, [userId])

  async function onSave() {
    setSaving(true)
    try {
      const values = await form.validateFields()

      const r1 = await patchCaptain(userId, {
        isCaptain: values.isCaptain,
      })
      if (!r1.ok) { message.error('تعذر حفظ بيانات الكابتن'); return }

      const r2 = await setCaptainPreferredLocations(
        userId,
        Array.isArray(values.preferredLocationIds) ? values.preferredLocationIds : []
      )
      if (!r2.ok) { message.error('تعذر حفظ المناطق المفضّلة'); return }

      message.success('تم الحفظ')
      await load()
    } catch (e) {
      console.error(e)
    } finally {
      setSaving(false)
    }
  }

  if (loading || !captain) return null

  const locationOptions = locations.map(l => ({
    value: l.id,
    label: l.nameAr || l.name || `#${l.id}`,
  }))

  const cardTitle = isMobile
    ? `${captain.name} (${captain.phone})`
    : `تعديل الكابتن — ${captain.name} (${captain.phone})`

  return (
    <div
      dir="rtl"
      style={{
        maxWidth: 720,
        margin: isMobile ? '12px auto' : '24px auto',
        padding: isMobile ? '0 8px' : '0 12px',
      }}
    >
      <Card
        title={cardTitle}
        styles={{ body: { padding: isMobile ? 12 : 24 } }}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="isCaptain" label="فعّال" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item
            name="preferredLocationIds"
            label="المناطق المفضّلة"
            tooltip="عند ورود طلب لإحدى هذه المناطق سيتم إعلام هذا الكابتن أولاً قبل البث للجميع. اتركها فارغة ليصل الكابتن فقط عند البث العام."
          >
            <Select
              mode="multiple"
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder="اختر المناطق التي يفضّلها الكابتن"
              options={locationOptions}
            />
          </Form.Item>
          <Space
            direction={isMobile ? 'vertical' : 'horizontal'}
            size="small"
            style={isMobile ? { width: '100%' } : undefined}
          >
            <Button
              type="primary"
              onClick={onSave}
              loading={saving}
              block={isMobile}
            >
              حفظ
            </Button>
            <Button onClick={() => navigate('/admin/captains')} block={isMobile}>إلغاء</Button>
            <Button
              onClick={() => navigate(`/admin/captains/${userId}/dashboard`)}
              block={isMobile}
            >
              فتح لوحة الكابتن
            </Button>
          </Space>
        </Form>
      </Card>
    </div>
  )
}
